import { DateTime } from 'luxon';
import type { JSX } from 'react';
import { LicensePlateType } from 'dg-web-shared/dto/LicensePlateType.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { Store, Updater, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Response } from 'dg-web-shared/lib/HttpResponse.ts';
import {
    getOrElse,
    isDefined,
    isUndefined,
    Maybe,
    thenElse,
} from 'dg-web-shared/lib/MaybeV2.ts';
import { Conditional } from 'dg-web-shared/lib/ReactHelpers.tsx';
import { rfidCardErrorText } from 'dg-web-shared/lib/RfidCardValidation.ts';
import * as StringConversions from 'dg-web-shared/lib/StringConversions.ts';
import * as Http from '../../api/Http.ts';
import * as AsyncRequest from '../../AsyncRequest.ts';
import * as ResultState from '../../clearance-permit-list/state/ResultState.ts';
import { LicensePlateTrippleEntry } from '../../common/components/LicensePlateTrippleEntry.tsx';
import * as GeneralTexts from '../../common/i18n/GeneralTexts.ts';
import { Translation } from '../../common/i18n/Text.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import * as SettingsState from '../../common/state/SettingsState.ts';
import * as PermitCreateState from '../../permit-detail/state/PermitCreateState.ts';
import * as PermitEditState from '../../permit-detail/state/PermitEditState.ts';
import { ButtonSpecialState } from '../../ui/buttons/IconButton.tsx';
import { SingleSelection } from '../../ui/labeled-elements/SingleSelection.tsx';
import { TextField } from '../../ui/labeled-elements/TextField.tsx';
import {
    Notification,
    NotificationBodyList,
    NotificationColor,
    NotificationIcon,
} from '../../ui/notifications/Notification.tsx';
import {
    ConfirmSaveHeader,
    ErrorHeader,
    FullSlideIn,
    FullSlideInLeftColumn,
    FullSlideInRightColumn,
    LoaderHeader,
    SlideInBody,
} from '../../ui/slidein/Slidein.tsx';
import * as ClearanceCreateTexts from '../i18n/ClearanceCreateTexts.ts';
import * as ClearanceCreateState from '../state/ClearanceCreateState.ts';
import {
    AdditionalInfos,
    ContractNumber,
    FromDateSelection,
    PersonalNumber,
    ToDateSelection,
} from './Shared.tsx';
import { languageFromString } from 'dg-web-shared/lib/Text.ts';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';

type ClearanceCreateDTO = Http.OperatorAccount.Clearances.ClearanceCreateDTO;

interface State {
    settings: SettingsState.State;
    layout: ClearanceCreateState.Layout.State;
    create: ClearanceCreateState.Create.State;
    createResponse: ClearanceCreateState.CreateResponse.State;
    validation: ClearanceCreateState.Validation.State;
    permitTypes: PermitTypeState.State;
    operator: OperatorDataState.State;
    carType: ClearanceCreateState.CarType.State;
    currentLogin: CurrentOperatorLoginState.State;
}

export interface Texts {
    HeaderCaption: Translation;
    LicensePlateType: Translation;
    Country: Translation;
    LicensePlate: Translation;
    Badge: Translation;
    CarType: Translation;
    MotorcycleType: Translation;
    LicensePlateAlreadyHasClearance: Translation;
    PermitType: Translation;
    CreateError: Translation;
    PriceMultiplierError: Translation;
    PriceMultiplier: Translation;
    CarTypeDescription: Translation;
}

namespace Derived {
    type MaybeDTO = Maybe<ClearanceCreateDTO>;

    export interface CreateRequest {
        bodyDisabled: boolean;
        formErrorsToShow: ClearanceCreateState.FormErrors;
        missingPriceMultipliersToShow: number[];
        overlaps: ClearanceCreateState.Overlaps;
        payload: MaybeDTO;
        confirmButtonSpecialState: Maybe<ButtonSpecialState>;
        requestReturnedError: boolean;
        requestIsPending: boolean;
        showLpEntry: boolean;
        showBadgeEntry: boolean;
    }

    const getButtonSpecialState = (
        state: State,
        payload: MaybeDTO,
    ): ButtonSpecialState | null => {
        if (isDefined(payload)) {
            return null;
        } else {
            return state.layout.confirmPressedWhileError
                ? ButtonSpecialState.ERROR
                : ButtonSpecialState.DISABLED;
        }
    };

    export const get = (state: State): CreateRequest => {
        const operatorData = state.operator.data;
        const validation = state.validation;
        const unvalidated = ClearanceCreateState.makeUnvalidatedPayload(
            state.create,
            operatorData ? operatorData.licensePlatePermitSettings : null,
            state.permitTypes.data,
            state.createResponse,
            validation,
        );
        const identMandant = PermitTypeState.getIdentificationTypes(
            state.permitTypes.data,
        );
        const identSelected =
            PermitTypeState.getIdentificationTypesGivenSelection(
                state.permitTypes.data,
                state.create.permitTypes,
            );
        const showLpEntry =
            (identMandant.length === 1 &&
                identMandant.indexOf('LicensePlate') > -1) ||
            identSelected.indexOf('LicensePlate') > -1;

        const data = validation.data;
        if (isDefined(data)) {
            const hasValidationErrors =
                data.overlappingClearances.lp.length > 0 ||
                data.overlappingClearances.badge.length > 0 ||
                data.permitTypesWithMissingPriceMultipliers.length > 0 ||
                (showLpEntry && !state.create.licensePlateValid);
            if (hasValidationErrors) {
                unvalidated.payload = null;
            }
        } else {
            unvalidated.payload = null;
        }

        return {
            bodyDisabled:
                state.layout.lpCountrySelectionOpen ||
                state.layout.permitTypeSelectionOpen ||
                state.layout.validFromDatePickerOpen ||
                state.layout.validToDatePickerOpen ||
                isDefined(state.layout.priceMultiplierOfPermitTypeOpen),
            formErrorsToShow: state.layout.confirmPressedWhileError
                ? unvalidated.formErrors
                : { lp: [], badge: [], other: [] },
            overlaps: thenElse(validation.data, d => d.overlappingClearances, {
                lp: [],
                badge: [],
            }),
            missingPriceMultipliersToShow:
                state.layout.confirmPressedWhileError && validation.data
                    ? validation.data.permitTypesWithMissingPriceMultipliers
                    : [],
            payload: unvalidated.payload,
            confirmButtonSpecialState: getButtonSpecialState(
                state,
                unvalidated.payload,
            ),
            requestIsPending: state.createResponse.pending,
            requestReturnedError: state.createResponse.statusCode.cls.error,
            showLpEntry,
            showBadgeEntry:
                (identMandant.length === 1 &&
                    identMandant.indexOf('Badge') > -1) ||
                identSelected.indexOf('Badge') > -1,
        };
    };
}

export const createClearance = AsyncRequest.request(
    Http.OperatorAccount.Clearances.create,
    (store: Store, res: Response): string => {
        ClearanceCreateState.CreateResponse.setResponse(store, res);
        if (res.statusCode.cls.success) {
            ClearanceCreateState.resetAllStates(store);
            PermitEditState.Validation.reset(store);
            PermitCreateState.Calc.reset(store);
            ResultState.refetchSameContext(store, true);
        }
        return 'ClearanceDetailActions-createClearance';
    },
);

// hack to avoid conflicts with Badge Feature
const getDefaultDate = (
    permitTypes: Maybe<PermitTypeState.PermitType[]>,
    startDate: DateTime,
): DateTime | null => {
    if (
        isDefined(permitTypes) &&
        permitTypes.length > 0 &&
        permitTypes[0].operatorId === 113
    ) {
        return startDate.plus({ years: 1 }).minus({ days: 1 });
    }
    return null;
};

export const Header = (p: { state: State; update: Updater; texts: Texts }) => {
    const createRequest = Derived.get(p.state);
    const cancel = () =>
        p.update((store: Store): string => {
            ClearanceCreateState.resetAllStates(store);
            return 'ClearanceCreate-cancel';
        });

    if (createRequest.requestIsPending) {
        return <LoaderHeader />;
    } else if (createRequest.requestReturnedError) {
        return (
            <ErrorHeader
                language={p.state.settings.language}
                title={p.texts.CreateError()}
                onCancel={cancel}
            />
        );
    } else {
        return (
            <ConfirmSaveHeader
                language={p.state.settings.language}
                subtitle={p.texts.HeaderCaption()}
                title={p.texts.HeaderCaption()}
                onCancel={cancel}
                confirmButtonSpecialState={
                    createRequest.confirmButtonSpecialState
                }
                onSave={() => {
                    if (isDefined(createRequest.payload)) {
                        if (isUndefined(createRequest.payload.validTo)) {
                            createRequest.payload.validTo = getDefaultDate(
                                p.state.permitTypes.data,
                                createRequest.payload.validFrom,
                            );
                        }
                        p.update(createClearance, createRequest.payload);
                    } else if (
                        createRequest.confirmButtonSpecialState ===
                        ButtonSpecialState.DISABLED
                    ) {
                        p.update(store =>
                            ClearanceCreateState.Layout.stateWrite(store, {
                                confirmPressedWhileError: true,
                            }),
                        );
                    }
                }}
            />
        );
    }
};

interface OverlapNotificationProps {
    createRequest: Derived.CreateRequest;
    language: string;
    overlaps: ClearanceCreateState.Overlap[];
}

export const OverlapNotification = (p: OverlapNotificationProps) => {
    const texts = ClearanceCreateTexts.texts[p.language];

    if (p.overlaps.length > 0) {
        return (
            <Notification
                icon={NotificationIcon.error}
                color={NotificationColor.error}
                title={texts.LicensePlateAlreadyHasClearance()}
            >
                <NotificationBodyList>
                    {p.overlaps.map(v => (
                        <OverlapEntity
                            entity={v}
                            lang={p.language}
                            key={v.id}
                        />
                    ))}
                </NotificationBodyList>
            </Notification>
        );
    } else {
        return null;
    }
};

export const OverlapEntity = (p: {
    entity: ClearanceCreateState.Overlap;
    lang: string;
}): JSX.Element => {
    return (
        <li>
            <span>{p.entity.description + ' '}</span>
            <span>
                {`(${Formatter.dayMonthYear(p.entity.validFrom)}` +
                    ` - ${Formatter.formatPermitToDateWithIndefinite(
                        p.entity.validTo,
                    )})`}
            </span>
        </li>
    );
};

const badgeError = (state: State): Maybe<string> => {
    const createRequest = Derived.get(state);
    if (createRequest.formErrorsToShow.other.indexOf('badgeEmpty') > -1) {
        return GeneralTexts.general[state.settings.language].MandatoryField();
    } else if (createRequest.formErrorsToShow.badge.length > 0) {
        return rfidCardErrorText(
            state.create.badgeLabelNr,
            languageFromString(state.settings.language),
        );
    }
};

function useClearanceCreateState() {
    const { storeState, update, store } = useStore(store => ({
        settings: new SettingsState.StateSlice(store).state,
        layout: ClearanceCreateState.Layout.get(store),
        create: ClearanceCreateState.Create.get(store),
        permitTypes: PermitTypeState.get(store),
        createResponse: ClearanceCreateState.CreateResponse.get(store),
        validation: ClearanceCreateState.Validation.get(store),
        operator: OperatorDataState.get(store),
        carType: ClearanceCreateState.CarType.get(store),
        currentLogin: CurrentOperatorLoginState.get(store),
    }));
    return { state: storeState, update, store };
}

function LicensePlateEntry() {
    const { state, update } = useClearanceCreateState();

    if (Derived.get(state).showLpEntry) {
        const txts = ClearanceCreateTexts.texts[state.settings.language];
        return (
            <div>
                <LicensePlateTrippleEntry
                    language={state.settings.language}
                    onLicensePlateNumberChange={(
                        v: string,
                        formValid: boolean,
                    ) => {
                        update(store =>
                            ClearanceCreateState.Create.stateWrite(store, {
                                licensePlateNumber: v,
                                licensePlateValid: formValid,
                            }),
                        );
                    }}
                    onCountrySlideInToggle={() =>
                        update(store =>
                            ClearanceCreateState.Layout.stateWrite(store, {
                                lpCountrySelectionOpen: true,
                            }),
                        )
                    }
                    onTypeSelect={(type: LicensePlateType) =>
                        update(store =>
                            ClearanceCreateState.Create.stateWrite(store, {
                                type,
                            }),
                        )
                    }
                    licensePlateType={state.create.type}
                    licensePlateNr={state.create.licensePlateNumber}
                    country={state.create.country}
                    showErrors={state.layout.confirmPressedWhileError}
                    outsideLicensePlateNrErrorGen={null}
                />
                <OverlapNotification
                    overlaps={Derived.get(state).overlaps.lp}
                    language={state.settings.language}
                    createRequest={Derived.get(state)}
                />
                <Conditional
                    c={
                        (state.operator.data &&
                            state.operator.data.settings.carTypeDescription) ||
                        false
                    }
                >
                    <div>
                        <TextField
                            inputType="text"
                            label={txts.CarTypeDescription()}
                            value={getOrElse<string>(
                                state.create.carTypeDescriptions[0],
                                state.carType.data &&
                                    state.carType.data.carTypes.length > 0
                                    ? state.carType.data.carTypes[0]
                                    : '',
                            )}
                            saveable={false}
                            maxLength={50}
                            onChange={(v: string) =>
                                update(store =>
                                    ClearanceCreateState.Create.setCarTypeDescription(
                                        store,
                                        { value: v, second: false },
                                    ),
                                )
                            }
                        />
                        <Conditional
                            c={state.create.carTypeDescriptions.length > 1}
                        >
                            <TextField
                                inputType="text"
                                label={txts.CarTypeDescription() + ' 2'}
                                value={getOrElse<string>(
                                    state.create.carTypeDescriptions[1],
                                    state.carType.data &&
                                        state.carType.data.carTypes.length > 0
                                        ? state.carType.data.carTypes[1]
                                        : '',
                                )}
                                saveable={false}
                                onChange={(v: string) =>
                                    update(store =>
                                        ClearanceCreateState.Create.setCarTypeDescription(
                                            store,
                                            { value: v, second: true },
                                        ),
                                    )
                                }
                            />
                        </Conditional>
                    </div>
                </Conditional>
            </div>
        );
    } else {
        return null;
    }
}

function BadgeEntry() {
    const { state, update } = useClearanceCreateState();

    if (Derived.get(state).showBadgeEntry) {
        const txts = ClearanceCreateTexts.texts[state.settings.language];
        return (
            <div>
                <TextField
                    inputType="text"
                    label={txts.Badge()}
                    value={getOrElse<string>(state.create.badgeLabelNr, '')}
                    saveable={false}
                    onChange={(v: string) =>
                        update(store =>
                            ClearanceCreateState.Create.stateWrite(store, {
                                badgeLabelNr:
                                    StringConversions.stripNonNumericCharacters(
                                        v,
                                    ),
                            }),
                        )
                    }
                    errorText={badgeError(state)}
                />
                <OverlapNotification
                    overlaps={Derived.get(state).overlaps.badge}
                    language={state.settings.language}
                    createRequest={Derived.get(state)}
                />
            </div>
        );
    } else {
        return null;
    }
}

function TypeSpecificEntries() {
    const { state, update } = useClearanceCreateState();

    const permitWhitelistInfoPerPermittype =
        state.operator.data &&
        state.operator.data.licensePlatePermitSettings
            .permitWhitelistInfoPerPermittype;
    const permitTypes = state.permitTypes.data;
    const missingMultipliers = Derived.get(state).missingPriceMultipliersToShow;
    const txt = ClearanceCreateTexts.texts[state.settings.language];
    if (!permitTypes) {
        return null;
    } else {
        const typesWithFactor = permitTypes.filter(
            pt =>
                isDefined(
                    pt.allowedPriceModifiers &&
                        pt.allowedPriceModifiers.length > 0,
                ) && state.create.permitTypes.indexOf(pt.id) > -1,
        );
        if (typesWithFactor.length === 0 && !permitWhitelistInfoPerPermittype) {
            return null;
        } else {
            return (
                <div>
                    {permitTypes
                        .filter(
                            pt => state.create.permitTypes.indexOf(pt.id) > -1,
                        )
                        .map(pt => {
                            const multiplier: Maybe<number> =
                                state.create.priceModifierIds[pt.id];
                            return (
                                <div key={pt.id}>
                                    <Conditional
                                        c={
                                            pt.allowedPriceModifiers &&
                                            pt.allowedPriceModifiers.length > 0
                                        }
                                    >
                                        <SingleSelection
                                            key={pt.id + 'pm'}
                                            focused={false}
                                            label={`${
                                                pt.description
                                            } ${txt.PriceMultiplier()}`}
                                            onClick={() =>
                                                update(store =>
                                                    ClearanceCreateState.Layout.stateWrite(
                                                        store,
                                                        {
                                                            priceMultiplierOfPermitTypeOpen:
                                                                pt.id,
                                                        },
                                                    ),
                                                )
                                            }
                                            selection={
                                                isDefined(multiplier)
                                                    ? pt.allowedPriceModifiers.find(
                                                          m =>
                                                              m.priceModifierId ===
                                                              multiplier,
                                                      )?.label
                                                    : null
                                            }
                                            errorText={
                                                missingMultipliers.indexOf(
                                                    pt.id,
                                                ) > -1
                                                    ? txt.PriceMultiplierError()
                                                    : null
                                            }
                                        />
                                    </Conditional>
                                    <Conditional
                                        c={
                                            permitWhitelistInfoPerPermittype ||
                                            false
                                        }
                                    >
                                        <AdditionalInfos
                                            mode={pt.additionalInfoMode}
                                            onChange={(v: string) =>
                                                update(store =>
                                                    ClearanceCreateState.Create.setAdditionalInfo(
                                                        store,
                                                        {
                                                            info: v,
                                                            permitTypeId: pt.id,
                                                        },
                                                    ),
                                                )
                                            }
                                            value={
                                                pt.additionalInfoMode === 'show'
                                                    ? pt.addInfo || ''
                                                    : state.create.infos[
                                                          pt.id
                                                      ] || ''
                                            }
                                            headerPrefix={pt.description}
                                            disabled={false}
                                        />
                                    </Conditional>
                                </div>
                            );
                        })}
                </div>
            );
        }
    }
}

export function ClearanceCreateSlideIn() {
    const { state, update, store } = useClearanceCreateState();

    function txt(): Texts {
        return ClearanceCreateTexts.texts[state.settings.language];
    }

    function generalTxt(): GeneralTexts.Texts {
        return GeneralTexts.general[state.settings.language];
    }

    function permitTypesError(): Maybe<string> {
        if (
            Derived.get(state).formErrorsToShow.other.indexOf('noPermitTypes') >
            -1
        ) {
            return generalTxt().MandatoryField();
        } else {
            return null;
        }
    }

    function renderSelectedPermitTypes() {
        if (state.create.permitTypes.length === 0) {
            return null;
        } else {
            return (
                <div>
                    {state.create.permitTypes.map((id: number) => {
                        const type = PermitTypeState.getById(store, id);
                        if (isDefined(type)) {
                            return <div key={type.id}>{type.description}</div>;
                        } else {
                            return null;
                        }
                    })}
                </div>
            );
        }
    }

    const permitWhitelistInfoPerPermittype =
        state.operator.data &&
        state.operator.data.licensePlatePermitSettings
            .permitWhitelistInfoPerPermittype;
    const whitelistCreate =
        state.currentLogin.data &&
        state.currentLogin.data.permissions.whitelistCreate;
    const additionalInfoMode: PermitTypeState.AdditionalInfoMode =
        state.permitTypes.data.filter(
            pt =>
                state.create.permitTypes.indexOf(pt.id) > -1 &&
                pt.additionalInfoMode === 'edit',
        ).length > 0
            ? 'edit'
            : 'show';
    return (
        <FullSlideIn open={state.layout.createEnabled} outsideBody>
            <SlideInBody disabled={Derived.get(state).bodyDisabled}>
                <FullSlideInLeftColumn>
                    <FromDateSelection
                        focused={false}
                        language={state.settings.language}
                        onClick={() =>
                            update(store =>
                                ClearanceCreateState.Layout.stateWrite(store, {
                                    validFromDatePickerOpen: true,
                                }),
                            )
                        }
                        validFrom={state.create.validFrom}
                        disabled={!whitelistCreate}
                    />
                    <ToDateSelection
                        focused={false}
                        language={state.settings.language}
                        onClick={() =>
                            update(store =>
                                ClearanceCreateState.Layout.stateWrite(store, {
                                    validToDatePickerOpen: true,
                                }),
                            )
                        }
                        onClear={
                            isDefined(state.create.validTo)
                                ? () =>
                                      update(store =>
                                          ClearanceCreateState.Create.stateWrite(
                                              store,
                                              { validTo: null },
                                          ),
                                      )
                                : null
                        }
                        validTo={thenElse(
                            state.create.validTo,
                            v => v,
                            getDefaultDate(
                                state.permitTypes.data,
                                state.create.validFrom,
                            ),
                        )}
                        disabled={!whitelistCreate}
                    />
                    <SingleSelection
                        focused={false}
                        label={txt().PermitType()}
                        selection={renderSelectedPermitTypes()}
                        onClick={() =>
                            update(store =>
                                ClearanceCreateState.Layout.stateWrite(store, {
                                    permitTypeSelectionOpen: true,
                                }),
                            )
                        }
                        errorText={permitTypesError()}
                    />
                    <PersonalNumber
                        onChange={(v: string) =>
                            update(store =>
                                ClearanceCreateState.Create.stateWrite(store, {
                                    personalNumber: v,
                                }),
                            )
                        }
                        value={state.create.personalNumber || ''}
                        disabled={!whitelistCreate}
                    />
                    <ContractNumber
                        onChange={(v: string) =>
                            update(store =>
                                ClearanceCreateState.Create.stateWrite(store, {
                                    contractNumber: v,
                                }),
                            )
                        }
                        value={state.create.contractNumber || ''}
                        disabled={!whitelistCreate}
                    />
                    <Conditional
                        c={
                            !permitWhitelistInfoPerPermittype &&
                            additionalInfoMode === 'edit'
                        }
                    >
                        <AdditionalInfos
                            onChange={(v: string) =>
                                update(store =>
                                    ClearanceCreateState.Create.setAdditionalInfo(
                                        store,
                                        { info: v },
                                    ),
                                )
                            }
                            mode="edit"
                            value={state.create.infos[0] || ''}
                            disabled={!whitelistCreate}
                        />
                    </Conditional>
                    <TypeSpecificEntries />
                </FullSlideInLeftColumn>
                <FullSlideInRightColumn>
                    <LicensePlateEntry />
                    <BadgeEntry />
                </FullSlideInRightColumn>
            </SlideInBody>
            <Header state={state} update={update} texts={txt()} />
        </FullSlideIn>
    );
}
